/* eslint-disable no-unused-vars */
import { Box } from '@mui/material'
import { makeStyles } from 'tss-react/mui'

/**
 * Fixed class name for list rows. This enables every cell to inspect
 * the row state (for hovering).
 *
 * @type {string}
 */
export const listRowClassName = 'Gaia-listRow'

/**
 * Applies `style` to an element whenever its row is hovered.
 *
 * The row (the cells' common ancestor) carries {@link listRowClassName} and
 * is `:hover`ed whenever the pointer is over any of its descendants, so a
 * CSS parent selector can react to the row-level hover without any React
 * state or re-render.
 *
 * @example
 * const styles = () => ({
 *   actions: {
 *     opacity: 0,
 *     ...onRowHover({ opacity: 1 }),
 *   },
 * })
 *
 * @param {Object} style the style to apply while the row is hovered
 * @returns {Object}
 */
export const onRowHover = style => ({
  [`.${listRowClassName}:hover &`]: style,
})

/**
 * Default styles to apply to all cell component.
 *
 * Needs `theme`, even though it isn't used.
 *
 * @param theme
 * @returns Object
 */
export const cellStyles = theme => ({
  root: {
    overflow: 'hidden',
  },
  row: {
    display: 'block',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
})

/**
 * Memoizes one tss-react styles hook per (additionalStyles, defaultStyles) pair.
 *
 * tss-react's `makeStyles()` must be called once per style definition — never
 * inside render — otherwise a new emotion stylesheet is set up on every render.
 * The cell/option HOCs are created once at module load with stable style
 * functions, so caching by those function references gives us exactly one hook
 * per cell/option component type. Prop-dependent styles still work: the props
 * are passed to the hook and forwarded to the style functions on every render,
 * while emotion caches the resulting rules by content.
 *
 * @type {WeakMap<Function, Map<Function, Function>>}
 */
const templateStylesHooks = new WeakMap()

const getTemplateStylesHook = (additionalStyles, defaultStyles) => {
  let byDefaultStyles = templateStylesHooks.get(additionalStyles)

  if (!byDefaultStyles) {
    byDefaultStyles = new Map()
    templateStylesHooks.set(additionalStyles, byDefaultStyles)
  }

  let useStyles = byDefaultStyles.get(defaultStyles)

  if (!useStyles) {
    useStyles = makeStyles()((theme, props) => ({
      ...defaultStyles(theme),
      ...additionalStyles(theme, props),
    }))
    byDefaultStyles.set(defaultStyles, useStyles)
  }

  return useStyles
}

/**
 * Keeps only the primitive entries of an object.
 *
 * tss-react memoizes the (expensive) emotion serialization per render via a
 * `useMemo` keyed on the style params. That cache only works when the params
 * are a flat record of primitives — `getDependencyArrayRef` turns such a record
 * into a stable content key. As soon as a single value is an object, array or
 * function it falls back to comparing the params by reference, and since cells
 * receive a fresh `props` object every render the styles get re-serialized for
 * every cell on every render.
 *
 * Style functions can only ever use primitive props as CSS values, so dropping
 * the non-primitive ones (`events`, callbacks, nested objects) from the style
 * params is loss-less and re-enables the memoization.
 *
 * @param {Object} props
 * @returns {Object} props with only string/number/boolean/null/undefined values
 */
const primitiveProps = (props = {}) => Object.fromEntries(
  Object.entries(props).filter(
    ([, value]) => value === null
        || (typeof value !== 'object' && typeof value !== 'function'),
  ),
)

/**
 * Default styling used for list cells.
 *
 * @param {Function} additionalStyles additional styles for the template
 * @param {Object} additionalProps    additional props for the styling
 * @param {Function} defaultStyles      default styles to pass to the template
 * @returns Object
 */
export const useTemplateStyles = (
  additionalStyles,
  additionalProps,
  defaultStyles = cellStyles,
) => {
  const useStyles = getTemplateStylesHook(additionalStyles, defaultStyles)

  return useStyles(primitiveProps(additionalProps)).classes
}

/**
 * Cell Higher Order Component
 *
 * HOC that will facilitate the common structure and styling of a cell in a list view.
 * `renderFn` is supposed to be a function responsible for rendering the actual
 * content (formerly called `propsToContent`).
 *
 * @param {Function} renderFn function to render the content
 * @param {Function} additionalStyles additional styles for the cell component
 *
 * @returns {*}
 */
const CellHOC = (renderFn, additionalStyles = () => ({})) => (props) => {
  const classes = useTemplateStyles(additionalStyles, props)
  const content = renderFn(classes, props)

  return <Box className={classes.root}>{content}</Box>
}

export default CellHOC
